import _ from "lodash";
import React from "react";
import { useTranslation, withTranslation } from "react-i18next";
import submit from "submit-form";
import { defaultFirebaseProjectId, S3 } from "../../../constants";
import { SearchDropdown } from "../../../parts/inputs/search-dropdown";
import Modal from "../../../parts/modal-new";
import SvgIcon from "../../../parts/SvgIcon";
import { RillContext } from "../../../rill/RillReact/share/base";
import FilterChips from "../../athlete-portal/calendar/FilterChips";

function parseOrganizationAthletesToOptions(
  allAthletesFromOrganization,
  onProgramKinds
) {
  const options = [];
  const uniqueAthleteIds = new Set();
  (allAthletesFromOrganization || [])
    .filter(({ athleteStatus }) => !athleteStatus?.inactive)
    .forEach(({ athleteId, athleteName, athleteGroups, athleteAvatar }) => {
      if (!uniqueAthleteIds.has(athleteId)) {
        uniqueAthleteIds.add(athleteId);
        options.push({
          value: athleteId,
          label: athleteName,
          onProgram: Boolean(onProgramKinds?.has(String(athleteId))),
          programKind: onProgramKinds?.get(String(athleteId)),
          groups: athleteGroups || [],
          avatar: athleteAvatar,
        });
      }
    });
  return options;
}

class SendModal extends React.Component {
  constructor(props, context) {
    super(props, context);
    const { locals } = context;

    const {
      activeUser: { athletes },
      allAthletesFromOrganization,
      organizationRoleName,
      clientsOnProgram,
    } = locals;
    const referAsClients = Boolean(locals.activeUser.referAsClients);
    // A push writes straight onto the client's calendar - if a team or
    // flexible program already governs it, that write can wreck it. Copying
    // (no push) is unaffected, so these athletes stay pickable, just flagged.
    const onProgramKinds = new Map(
      (clientsOnProgram || []).map(({ userId, kind }) => [userId, kind])
    );

    // A Team Coach never holds clients of their own — same reason the athlete list
    // defaults them straight to the org-wide roster — so there's no "my athletes" vs
    // "all organization athletes" split to offer them: their own list IS the org-wide
    // list, and the separate "All Organization Athletes" toggle would be a same-result
    // duplicate. Checked independently of the `isAllowToViewAllOrganizationsAthletes`
    // prop, since some callers of this modal never pass it at all.
    const isTeamCoach = organizationRoleName === "Team Coach";
    const isAllowToViewAllOrganizationsAthletes =
      Boolean(props.isAllowToViewAllOrganizationsAthletes) && !isTeamCoach;

    let athletesOptions;
    if (isTeamCoach) {
      athletesOptions = parseOrganizationAthletesToOptions(
        allAthletesFromOrganization,
        onProgramKinds
      );
    } else {
      athletesOptions = [];
      athletes
        .filter(({ status }) => !status?.inactive)
        .forEach((athlete) => {
          athletesOptions.push({
            value: athlete._id,
            label: athlete.name,
            onProgram: onProgramKinds.has(String(athlete._id)),
            programKind: onProgramKinds.get(String(athlete._id)),
            groups: athlete.groups || [],
            avatar: athlete.avatar,
          });
        });
    }

    const allAthletesFromOrganizationOptions =
      allAthletesFromOrganization && isAllowToViewAllOrganizationsAthletes
        ? parseOrganizationAthletesToOptions(
            allAthletesFromOrganization,
            onProgramKinds
          )
        : [];

    this.state = {
      selectedAthletes: [],
      activeAthleteCycles: [],
      athletesOptions,

      showConfirm: false,
      submited: false,
      referAsClients,
      allAthletesFromOrganizationOptions,
      isAllowToViewAllOrganizationsAthletes,
      isSearching: false,
    };
    this.handleClearAll = this.handleClearAll.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.clearFilters = this.clearFilters.bind(this);
    this.searchAthletes = this.searchAthletes.bind(this);
    this.generateAthleteListItem = this.generateAthleteListItem.bind(this);
  }

  generateAthleteListItem(athlete, getImgUrl) {
    return (
      <li
        key={`${athlete.value}`}
        onClick={
          this.state.selectedAthletes.find(
            (item) => item.value === athlete.value
          )
            ? () =>
                this.handleAthleteChange(
                  this.state.selectedAthletes.filter(
                    (ath) => ath.value !== athlete.value
                  )
                )
            : () =>
                this.handleAthleteChange([
                  ...this.state.selectedAthletes,
                  athlete,
                ])
        }
      >
        <img
          src={getImgUrl(athlete)}
          style={
            getImgUrl(athlete) === "/img/new-icons/user-circle.svg"
              ? {
                  backgroundColor: "var(--background-color)",
                  padding: "8px",
                }
              : {}
          }
        />
        {athlete.label}
      </li>
    );
  }

  // function to filter the athletes in the mobile screen
  searchAthletes = (value) => {
    const label = value.toString().toLowerCase();
    const originalList =
      this.state.allAthletesFromOrganizationOptions.length > 0
        ? this.state.allAthletesFromOrganizationOptions.sort((a, b) =>
            a.label.localeCompare(b.label)
          )
        : this.state.athletesOptions;

    const filteredArray = originalList.filter((item) =>
      item.label.toLowerCase().startsWith(label)
    );

    this.setState({
      athletesOptions: value ? filteredArray : originalList,
    });
  };

  isAFilterActive() {
    const { athletesGroups } = this.context.locals.activeUser;
    return (
      this.state.allAthletes ||
      this.state.allOrganizationAthletes ||
      athletesGroups?.some((elem) => this.state[elem.name])
    );
  }

  // if there is not selected athletes disable filters
  clearFilters() {
    if (this.state.selectedAthletes.length === 0 && this.isAFilterActive()) {
      const filters = { allAthletes: false, allOrganizationAthletes: false };
      const { athletesGroups } = this.context.locals.activeUser;
      athletesGroups &&
        athletesGroups.forEach((elem) => (filters[elem.name] = false));
      this.setState(filters);
    }
  }

  handleInputChange(value, name) {
    const {
      selectedAthletes,
      athletesOptions,
      allAthletesFromOrganizationOptions,
      isAllowToViewAllOrganizationsAthletes,
    } = this.state;
    let selection = [...selectedAthletes];

    const currentCoachAthletes =
      allAthletesFromOrganizationOptions.length > 0 &&
      isAllowToViewAllOrganizationsAthletes
        ? allAthletesFromOrganizationOptions.filter((athlete) =>
            athletesOptions.some((option) => option.value === athlete.value)
          )
        : athletesOptions;

    if (name === "allAthletes" && value) {
      // select all athletes
      selection = currentCoachAthletes;
    } else if (
      (name === "allAthletes" || name === "allOrganizationAthletes") &&
      !value
    ) {
      // clean selection
      selection = [];
    } else if (name === "allOrganizationAthletes" && value) {
      selection = allAthletesFromOrganizationOptions;
    } else if (value) {
      // filter by group

      selection =
        allAthletesFromOrganizationOptions.length > 0 &&
        isAllowToViewAllOrganizationsAthletes
          ? [
              ...selection,
              ...allAthletesFromOrganizationOptions.filter((elem) =>
                elem.groups.includes(name)
              ),
            ]
          : [
              ...selection,
              ...this.state.athletesOptions.filter((elem) =>
                elem.groups.includes(name)
              ),
            ];
    } else {
      // unselect by group
      selection = selection.filter((elem) => !elem.groups.includes(name));
    }

    this.setState({
      [name]: value,
      selectedAthletes: _.uniq(selection),
      refresh: !this.state.refresh,
    });
  }

  handleAthleteChange(recipient) {
    // If user is removed from list
    if (recipient[0] === undefined)
      this.setState(
        {
          selectedAthletes: [],
          activeAthleteCycles: [],
        },
        () => {
          this.clearFilters();
        }
      );
    else {
      // Setting Active cycle to iterate through.
      const { athleteCycles } = this.context.locals;
      // These are the filtered cycles for the selected athlete.
      athleteCycles.filter((cycle) => {
        // If the selected athlete is the owner of the current cycle its added to their active
        if (cycle.owner._id === recipient[0].value) {
          this.setState((prevState) => ({
            activeAthleteCycles: [
              ...prevState.activeAthleteCycles,
              {
                _id: cycle._id,
                name: cycle.name,
              },
            ],
          }));
        }
        // Set the selected athletes
        this.setState({
          selectedAthletes: recipient,
        });
      });
    }
  }

  handleCycleKindChange(value) {
    this.setState({ cycleKind: value });
  }

  send({
    cycleId,
    destinationCycleId,
    programOrder,
    selectedAthletes,
    push,
    submited,
    e,
  }) {
    e.preventDefault();
    if (submited) return;
    this.setState({ submited: true });
    const { activeUserToRequest, activeUser } = this.context.locals;
    submit(`/actions/cycle/send`, {
      method: "POST",
      body: {
        cycleId,
        destinationCycleId,
        programOrder,
        recipients: selectedAthletes.map((ath) => ath.value),
        push,
        avatar:
          !!activeUser.avatar && !!activeUser.avatar.file
            ? activeUser.avatar.file
            : null,
        firebaseProjectId: activeUserToRequest.firebaseProjectId
          ? activeUserToRequest.firebaseProjectId
          : defaultFirebaseProjectId,
      },
    });
  }

  handleClearAll() {
    this.setState({ selectedAthletes: [] }, () => {
      this.clearFilters();
    });
  }

  showSend() {
    const {
      selectedAthletes,
      athletesOptions,
      activeAthleteCycles,
      destinationCycleId,
      cycleKind = "new",
      isAllowToViewAllOrganizationsAthletes,
      allAthletesFromOrganizationOptions,
      allAthletes,
      allOrganizationAthletes,
      isSearching,
    } = this.state;
    const { locals } = this.context;
    const referAsClients = locals.activeUser.referAsClients;
    const {
      query = {},
      athleteCycles,
      activeUser: { athletesGroups },
    } = locals;
    const { cycleId, programOrder } = query;

    const cycle = _.find(athleteCycles, { _id: cycleId });
    const program =
      programOrder && cycle && _.find(cycle.programs, { order: programOrder });
    const hasRequiredInputs = !!cycle && (programOrder ? !!program : true);

    const existingCycleOptions = _.map(activeAthleteCycles, (cycle) => ({
      value: cycle._id,
      label: cycle.name,
    }));

    const isValid =
      hasRequiredInputs &&
      selectedAthletes.length > 0 &&
      (cycleKind === "new" || destinationCycleId);
    const blockedSelection = selectedAthletes.filter((ath) => ath.onProgram);
    const getImgUrl = (athlete) => {
      if (
        selectedAthletes.find((selected) => selected.value === athlete.value)
      ) {
        return "/img/new-icons/tick-circle.svg";
      } else {
        if (athlete.avatar) {
          return `${S3.PATH}${athlete.avatar.s3Path}`;
        } else return "/img/new-icons/user-circle.svg";
      }
    };

    return (
      <Modal
        toClear={["cycleId", "programOrder", "recipient", "sendToAnother"]}
        className="send-program-modal"
        title={
          programOrder
            ? this.props.t("sendModal.sendBlock")
            : this.props.t("sendModal.sendProgram")
        }
      >
        {["team", "flexible"].map((kind) => {
          const names = blockedSelection
            .filter((ath) => ath.programKind === kind)
            .map((ath) => ath.label);
          if (!names.length) return null;
          return (
            <div
              key={kind}
              className="push-warning mx-6 mt-4 rounded-md px-3 py-2 text-sm"
              role="alert"
            >
              <strong>
                {this.props.t(`sendModal.pushWarn.${kind}.title`, {
                  names: names.join(", "),
                  count: names.length,
                })}
              </strong>
              <p>{this.props.t(`sendModal.pushWarn.${kind}.secondary`)}</p>
              <p>{this.props.t(`sendModal.pushWarn.${kind}.howTo`)}</p>
            </div>
          );
        })}
        {hasRequiredInputs && (
          <div className="body">
            <div className="left">
              {/* select group for desktop */}
              <div>
                <SelectGroupForDesktop
                  handleInputChange={this.handleInputChange}
                  state={this.state}
                  allAthletesFromOrganizationOptions={
                    allAthletesFromOrganizationOptions
                  }
                  isAllowToViewAllOrganizationsAthletes={
                    isAllowToViewAllOrganizationsAthletes
                  }
                  athletesGroups={athletesGroups}
                />
                {/* select group for mobile */}
                <SelectGroupForMobile
                  handleInputChange={this.handleInputChange}
                  state={this.state}
                  allAthletesFromOrganizationOptions={
                    allAthletesFromOrganizationOptions
                  }
                  isAllowToViewAllOrganizationsAthletes={
                    isAllowToViewAllOrganizationsAthletes
                  }
                  athletesGroups={athletesGroups}
                />
                <SearchAthletesForMobile
                  handleInputChange={this.handleInputChange}
                  allAthletes={allAthletes}
                  allAthletesFromOrganizationOptions={
                    allAthletesFromOrganizationOptions
                  }
                  allOrganizationAthletes={allOrganizationAthletes}
                  searchAthletes={this.searchAthletes}
                  referAsClients={referAsClients}
                />
                <section>
                  <header>
                    {isSearching ? (
                      <>
                        <input
                          type="search"
                          className="mobile-hidden"
                          name="search"
                          placeholder={this.props.t(
                            "sendModal.searchPlaceholder"
                          )}
                          onChange={(e) => this.searchAthletes(e.target.value)}
                          data-expandable="true"
                        />
                        <SvgIcon
                          role="button"
                          data-button-type="text-white"
                          data-icon-only="true"
                          data-icon
                          sz="small"
                          onClickFn={() => {
                            this.searchAthletes("");
                            this.setState({
                              isSearching: false,
                            });
                          }}
                          src="/img/new-icons/close-search.svg"
                        />
                      </>
                    ) : (
                      <p
                        className="p4 "
                        onClick={() => this.setState({ isSearching: true })}
                      >
                        <span>
                          {this.props.t("sendModal.sendACopyOf")}{" "}
                          <strong>
                            {programOrder ? program.name : cycle.name}
                          </strong>{" "}
                          {this.props.t("sendModal.toOneOrMultiple")}{" "}
                          {this.state.referAsClients
                            ? this.props.t("sendModal.clients")
                            : this.props.t("sendModal.athletes")}
                          . {this.props.t("sendModal.thisWillCreateCopy")}{" "}
                          {this.state.referAsClients
                            ? this.props.t("sendModal.clientPortal")
                            : this.props.t("sendModal.athletePortal")}{" "}
                          {this.props.t("sendModal.portal")}
                        </span>
                      </p>
                    )}
                  </header>

                  {/* list for desktop (render all the athletes) */}
                  <ul className="athlete-select ">
                    {athletesOptions.length > 0 &&
                      athletesOptions.map((athlete) => {
                        return this.generateAthleteListItem(athlete, getImgUrl);
                      })}
                  </ul>
                </section>
              </div>
              {programOrder && selectedAthletes.length > 0 && (
                <div className="Add-Cycle">
                  <p className="p4">
                    {this.props.t("sendModal.chooseCycleToCopyTo")}
                  </p>
                  <div className="group">
                    <label className="radio-input">
                      <input
                        name="cycleKind"
                        type="radio"
                        checked={cycleKind === "new"}
                        onChange={() => this.handleCycleKindChange("new")}
                      />

                      <span className="text">
                        {this.props.t("sendModal.createNew")}
                      </span>
                    </label>
                    {selectedAthletes.length === 1 && (
                      <label className="radio-input">
                        <input
                          name="cycleKind"
                          type="radio"
                          checked={cycleKind === "existing"}
                          onChange={() =>
                            this.handleCycleKindChange("existing")
                          }
                          disabled={selectedAthletes.length > 1}
                          readOnly={selectedAthletes.length > 1}
                        />

                        <span className="text">
                          {this.props.t("sendModal.chooseExisting")}
                        </span>
                      </label>
                    )}
                  </div>
                </div>
              )}
              {selectedAthletes.length === 1 && cycleKind === "existing" && (
                <div className="athlete-select" style={{ marginTop: "20px" }}>
                  <SearchDropdown
                    value={
                      existingCycleOptions.find(
                        (option) => option.value === destinationCycleId
                      )?.label || null
                    }
                    options={existingCycleOptions}
                    handleChange={(cycle) => {
                      this.setState({ destinationCycleId: cycle.value });
                    }}
                    placeholder={this.props.t("sendModal.selectBlock")}
                    valueKey="label"
                  />
                </div>
              )}
            </div>
            <div className="right">
              <header>
                <h3>
                  {this.state.referAsClients
                    ? this.props.t("sendModal.selectedClients")
                    : this.props.t("sendModal.selectedAthletes")}
                </h3>
                <button
                  data-button-type="secondary"
                  disabled={selectedAthletes.length === 0}
                  onClick={this.handleClearAll}
                >
                  {this.props.t("sendModal.clearAll")}
                </button>
              </header>
              <ul className="mb-6">
                <FilterChips
                  filters={selectedAthletes.map((athlete) => ({
                    text: athlete.label,
                    id: athlete.value,
                  }))}
                  clearFilter={(id) => {
                    this.setState({
                      selectedAthletes: selectedAthletes.filter(
                        (athlete) => athlete.value !== id
                      ),
                    });
                  }}
                />
              </ul>
              <div>
                {!hasRequiredInputs && (
                  <div>
                    <p>{this.props.t("sendModal.noCycleOrProgramToSend")}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <footer>
          <div>
            <p className="chip warning text-[0.75rem]">
              {this.props.t("sendModal.pushWarning", {
                role: this.state.referAsClients
                  ? this.props.t("sendModal.clients")
                  : this.props.t("sendModal.athletes"),
              })}
            </p>
            <p className="chip warning text-[0.75rem]">
              {this.props.t("sendModal.copyToWarning", {
                role: this.state.referAsClients
                  ? this.props.t("sendModal.clients")
                  : this.props.t("sendModal.athletes"),
              })}
            </p>
          </div>

          <div>
            <button
              data-button-type="outline"
              disabled={!isValid || undefined}
              type="submit"
              onClick={(e) => this.setState({ showConfirm: true })}
            >
              <span>{this.props.t("sendModal.push")}</span>
            </button>
            <button
              type="submit"
              data-button-type="primary"
              disabled={!isValid || undefined}
              onClick={(e) =>
                this.send({
                  cycleId,
                  destinationCycleId,
                  programOrder,
                  selectedAthletes,
                  submited: this.state.submited,
                  e,
                })
              }
            >
              {this.props.t("sendModal.copy")}
            </button>
          </div>
        </footer>
      </Modal>
    );
  }

  showConfirmation() {
    const { destinationCycleId } = this.state;
    const { locals } = this.context;
    const { activeUser } = locals;
    const referAsClients = Boolean(activeUser.referAsClients);
    const { query } = locals;

    const { cycleId, programOrder } = query;
    const { selectedAthletes } = this.state;
    return (
      <Modal
        width="600px"
        title={this.props.t("sendModal.confirmSendAndPush")}
        className={"confirm-push-block"}
      >
        <div className="body">
          <h4>{this.props.t("sendModal.confirmPushHeading")}</h4>
          <ul>
            <li>
              {this.props.t("sendModal.confirmPoint1", {
                role: referAsClients
                  ? this.props.t("sendModal.clients")
                  : this.props.t("sendModal.athletes"),
              })}
            </li>
            <li>{this.props.t("sendModal.confirmPoint2")}</li>
            <li>
              {this.props.t("sendModal.confirmPoint3", {
                role: referAsClients
                  ? this.props.t("sendModal.clients")
                  : this.props.t("sendModal.athletes"),
              })}
            </li>
          </ul>
        </div>

        <footer>
          <a
            role="button"
            data-button-type="secondary"
            data-action="cancel"
            href="/actions/refresh?modal="
          >
            {this.props.t("sendModal.cancel")}
          </a>
          <button
            role="button"
            data-button-type="primary"
            onClick={(e) =>
              this.send({
                cycleId,
                destinationCycleId,
                programOrder,
                selectedAthletes,
                push: true,
                submited: this.state.submited,
                e,
              })
            }
          >
            {this.props.t("sendModal.confirm")}
          </button>
        </footer>
      </Modal>
    );
  }

  render() {
    const { showConfirm } = this.state;

    if (!showConfirm) {
      return this.showSend();
    }

    return this.showConfirmation();
  }
}

SendModal.contextType = RillContext;

export default withTranslation("coaching-portal")(SendModal);

const SelectGroupForDesktop = ({
  handleInputChange,
  state,
  allAthletesFromOrganizationOptions,
  isAllowToViewAllOrganizationsAthletes,
  athletesGroups,
}) => {
  const { t } = useTranslation("coaching-portal");
  return (
    <fieldset className="select-by-group">
      <legend className="select-title">{t("sendModal.selectByGroup")}</legend>
      <div className="select-group-filters">
        <div
          className="select-group-filter"
          onClick={() => handleInputChange(!state.allAthletes, "allAthletes")}
        >
          <img
            src={`/img/new-icons/${
              state.allAthletes ? "checked" : "unchecked"
            }-square.svg`}
          />
          {state.referAsClients
            ? t("sendModal.allClients")
            : t("sendModal.allAthletes")}
        </div>
        {allAthletesFromOrganizationOptions.length > 0 &&
          isAllowToViewAllOrganizationsAthletes && (
            <div
              className="select-group-filter"
              onClick={() =>
                handleInputChange(
                  !state.allOrganizationAthletes,
                  "allOrganizationAthletes"
                )
              }
            >
              <img
                src={`/img/new-icons/${
                  state.allOrganizationAthletes ? "checked" : "unchecked"
                }-square.svg`}
              />
              {state.referAsClients
                ? t("sendModal.allOrganizationClients")
                : t("sendModal.allOrganizationAthletes")}
            </div>
          )}
        {athletesGroups &&
          athletesGroups.map((elem) => (
            <div
              className="select-group-filter"
              onClick={() => handleInputChange(!state[elem.name], elem.name)}
            >
              <img
                src={`/img/new-icons/${
                  state[elem.name] ? "checked" : "unchecked"
                }-square.svg`}
              />
              {elem.name}
            </div>
          ))}
      </div>
    </fieldset>
  );
};

const SelectGroupForMobile = ({
  handleInputChange,
  state,
  allAthletesFromOrganizationOptions,
  isAllowToViewAllOrganizationsAthletes,
  athletesGroups,
}) => {
  const { t } = useTranslation("coaching-portal");
  return (
    <div className="select-by-group-mobile force-only-show-mobile">
      <button
        type="button"
        className={
          state.allAthletes
            ? `select-group-filter-mobile active-select`
            : `select-group-filter-mobile`
        }
        onClick={() => handleInputChange(!state.allAthletes, "allAthletes")}
      >
        {state.referAsClients
          ? t("sendModal.allClients")
          : t("sendModal.allAthletes")}
      </button>
      {allAthletesFromOrganizationOptions.length > 0 &&
        isAllowToViewAllOrganizationsAthletes && (
          <button
            type="button"
            className={
              state.allOrganizationAthletes
                ? `select-group-filter-mobile active-select`
                : `select-group-filter-mobile`
            }
            onClick={() =>
              handleInputChange(
                !state.allOrganizationAthletes,
                "allOrganizationAthletes"
              )
            }
          >
            {state.referAsClients
              ? t("sendModal.allOrganizationClients")
              : t("sendModal.allOrganizationAthletes")}
          </button>
        )}
      {athletesGroups &&
        athletesGroups.map((elem) => (
          <button
            type="button"
            className={
              state[elem.name]
                ? `select-group-filter-mobile active-select`
                : `select-group-filter-mobile`
            }
            onClick={() => handleInputChange(!state[elem.name], elem.name)}
          >
            {elem.name}
          </button>
        ))}
    </div>
  );
};

const SearchAthletesForMobile = ({
  handleInputChange,
  allAthletes,
  allAthletesFromOrganizationOptions,
  allOrganizationAthletes,
  searchAthletes,
  referAsClients,
}) => {
  const { t } = useTranslation("coaching-portal");
  const isAllAthletesFromOrganizationOptionsEmpty =
    allAthletesFromOrganizationOptions.length === 0;

  const handleClick = () => {
    if (isAllAthletesFromOrganizationOptionsEmpty) {
      handleInputChange(!allAthletes, "allAthletes");
    } else {
      handleInputChange(!allOrganizationAthletes, "allOrganizationAthletes");
    }
  };

  const athletesToSelect = isAllAthletesFromOrganizationOptionsEmpty
    ? allAthletes
    : allOrganizationAthletes;
  return (
    <div className="force-only-show-mobile" id="search-athlete-select">
      <input
        name="search"
        type="search"
        // value={search}
        onChange={(e) => searchAthletes(e.target.value)}
        placeholder={
          referAsClients
            ? t("sendModal.searchClient")
            : t("sendModal.searchAthlete")
        }
        autoComplete="off"
      />
      <div>
        <img
          src={
            athletesToSelect
              ? "/img/new-icons/checked-square.svg"
              : "/img/new-icons/unchecked-square.svg"
          }
          onClick={handleClick}
        />
        <span>{t("sendModal.selectAll")}</span>
      </div>
    </div>
  );
};
